import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Lock,
  RefreshCcw,
  ShieldAlert,
} from 'lucide-react';

import { AuditIssueCard } from '@/features/audit-issues';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { BatchStatusBadge, type BatchStatus } from '@/components/efs/BatchStatusBadge';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { ApiClientError } from '@/lib/api-client';
import {
  isCatalogedIssueCode,
  issueCodeKeys,
  useIssueText,
  type IssuePresentation,
} from '@/features/audit-issues';
// Imported from its file (like LocalizedApiError above) rather than the
// audit-issues index, so index.ts keeps its pinned UI-evidence hash.
import { useTargetRecord } from '@/features/audit-issues/AuditTargetLink';

import { downloadMonthlyReportArtifact } from './api';
import { ReportSettingsCreatePanel } from './ReportSettingsCreatePanel';
import {
  useExternalMonthlyReport,
  useGenerateMonthlyReportArtifact,
  useInternalMonthlyAnalysis,
  useMonthlyReportArtifacts,
  useMonthlyReportCommercialTerms,
  useMonthlyReportPrecheck,
  useMonthlyReportProfiles,
  useLockMonthlyReportCommercialTerms,
  useLockMonthlyReportProfile,
  useSupersedeMonthlyReportCommercialTerms,
  useSupersedeMonthlyReportProfile,
  useValuationComparison,
} from './useMonthlyReports';
import type {
  AbnormalItem,
  AbnormalSummary,
  DestinationSummary,
  EstimateProjectionDto,
  ExternalMonthlyReportDto,
  ExternalMonthlyReportSnapshotDto,
  FinancialFeeDto,
  FinancialLineDto,
  InboundCategorySummary,
  InternalMonthlyAnalysisDto,
  DismantlingOutputSummary,
  MaterialShipmentSummary,
  MaterialUnitPrice,
  MonthlyReportMassBalanceSummary,
  MonthlyReportArtifactDto,
  MonthlyReportCommercialTermsDto,
  MonthlyReportPrecheckDto,
  MonthlyReportProfileDto,
  MonthlyReportVariant,
  SettlementProjectionDto,
  OutboundDestinationSummary,
  ReportBasicInfo,
  ValuationComparisonDto,
  ValuationComparisonLine,
} from './types';

const NS = 'reports';

type ReportTab = 'overview' | 'settlement' | 'financial' | 'issues' | 'history';

const VARIANTS: MonthlyReportVariant[] = [
  'external_operations',
  'customer_settlement',
  'processor_invoice',
  'internal_estimate',
  'internal_full',
];

/** The readiness rail lists at most this many issues so a long list cannot push the generate cards out of view. */
const READINESS_ISSUE_PREVIEW = 3;

// The position is part of the key: precheck can report the same code for the
// same monthly batch more than once (e.g. a missing profile from both the
// operational and a financial check), and the list is rebuilt on every refetch.
const issueKey = (issue: IssuePresentation, index: number) =>
  `${index}-${issue.issue_code}-${issue.target?.record_id ?? issue.detected_at ?? issue.target?.record_code}`;

const MONEY_VARIANTS = new Set<MonthlyReportVariant>([
  'customer_settlement',
  'processor_invoice',
  'internal_estimate',
  'internal_full',
]);

const viewPermissionFor = (variant: MonthlyReportVariant): string => {
  switch (variant) {
    case 'external_operations':
      return 'report.external_monthly.view';
    case 'customer_settlement':
      return 'report.customer_settlement.view';
    case 'processor_invoice':
      return 'report.processor_invoice.view';
    case 'internal_estimate':
    case 'internal_full':
      return 'report.internal_analysis.view';
  }
};

const generatePermissionFor = (variant: MonthlyReportVariant): string => {
  switch (variant) {
    case 'external_operations':
      return 'report.external_monthly.generate';
    case 'customer_settlement':
      return 'report.customer_settlement.generate';
    case 'processor_invoice':
      return 'report.processor_invoice.generate';
    case 'internal_estimate':
    case 'internal_full':
      return 'report.internal_analysis.generate';
  }
};

const formatAmount = (value: string | null): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const formatKgValue = (value: string | null): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(n);
};

const shortHash = (hash: string | null | undefined): string => {
  if (!hash) return '-';
  const normalized = hash.replace(/^sha256:/, '');
  return `${normalized.slice(0, 12)}…`;
};

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null) return '-';
  return new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 1 }).format(bytes / 1024) + ' KiB';
};

const newIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const artifactFileName = (artifact: MonthlyReportArtifactDto): string =>
  `${artifact.variant}_v${artifact.version}_${artifact.output_locale}.xlsx`;

const settingStatusVariant = (status: string): 'default' | 'destructive' | 'outline' => {
  switch (status) {
    case 'locked':
      return 'default';
    case 'draft':
      return 'outline';
    default:
      return 'destructive';
  }
};

type OperationalPreview = {
  basic_info: ReportBasicInfo;
  confirmed_trip_count?: number | null;
  source_updated_at?: string | null;
  summary?: MonthlyReportMassBalanceSummary | null;
  mass_balance_summary?: MonthlyReportMassBalanceSummary | null;
  inbound_category_inspection: InboundCategorySummary[];
  dismantling_output_summary?: DismantlingOutputSummary[];
  final_material_shipment_summary?: MaterialShipmentSummary[];
  final_destinations?: DestinationSummary[];
  outbound_destination_summary: OutboundDestinationSummary[];
  notes?: string | null;
  abnormal_summary?: AbnormalSummary;
};

const sumKg = (values: string[]): string => {
  const total = values.reduce((acc, value) => acc + (Number.parseFloat(value) || 0), 0);
  return total.toFixed(3);
};

const summaryFromReport = (report: OperationalPreview): MonthlyReportMassBalanceSummary =>
  report.summary ??
  report.mass_balance_summary ?? {
    inbound_kg: sumKg(report.inbound_category_inspection.map((row) => row.net_kg)),
    dismantling_output_kg: report.dismantling_output_summary
      ? sumKg(report.dismantling_output_summary.map((row) => row.produced_kg))
      : '',
    shipped_kg: sumKg((report.final_material_shipment_summary ?? []).map((row) => row.total_kg)),
    closing_inventory_kg: null,
    approved_adjustment_kg: null,
    approved_loss_kg: null,
    difference_kg: null,
    status: null,
  };

const summaryApprovedAdjustmentKg = (
  summary: ReturnType<typeof summaryFromReport>,
): string | null => summary.approved_adjustment_kg ?? summary.approved_loss_kg ?? null;

const localizedStatus = (
  t: ReturnType<typeof useTranslation>['t'],
  group: 'receipt_status' | 'resource_chain_status' | 'mass_balance_status',
  value: string | null | undefined,
): string => {
  if (!value) return '-';
  return t(`efs.reports.monthly.status.${group}.${value}`, { defaultValue: value });
};

const BasicInfoCard = ({ info }: { info: ReportBasicInfo }) => {
  const { t } = useTranslation(NS);
  const rows: Array<{ label: string; value: string }> = [
    { label: t('efs.reports.monthly.basic.year_month'), value: info.year_month },
    {
      label: t('efs.reports.monthly.basic.status'),
      value: t(`efs.common.status.${info.status}`, { ns: 'common' }),
    },
    { label: t('efs.reports.monthly.basic.facility'), value: info.facility_name },
    { label: t('efs.reports.monthly.basic.company'), value: info.company_name },
    { label: t('efs.reports.monthly.basic.closed_at'), value: formatDateTime(info.closed_at) },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('efs.reports.monthly.basic.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
          {rows.map((r) => (
            <div
              key={r.label}
              className="flex justify-between gap-4 border-b border-efs-border py-1.5"
            >
              <dt className="text-sm text-efs-text-muted">{r.label}</dt>
              <dd className="text-right text-sm font-medium text-efs-text-primary">{r.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const ErrorAlert = ({ error, title }: { error: unknown; title?: string }) => {
  const { t } = useTranslation(NS);
  return (
    <Alert variant="destructive">
      <AlertTitle>{title ?? t('efs.reports.monthly.error.title')}</AlertTitle>
      <LocalizedApiError error={error} />
    </Alert>
  );
};

const KpiGrid = ({ report }: { report: OperationalPreview }) => {
  const { t } = useTranslation(NS);
  const summary = summaryFromReport(report);
  const confirmedTrips = report.confirmed_trip_count ?? summary.confirmed_trip_count;
  const weightKpis = [
    { key: 'inbound_kg', value: formatKgValue(summary.inbound_kg), unit: 'kg' },
    {
      key: 'dismantling_output_kg',
      value: formatKgValue(summary.dismantling_output_kg),
      unit: 'kg',
    },
    { key: 'shipped_kg', value: formatKgValue(summary.shipped_kg), unit: 'kg' },
    { key: 'closing_inventory_kg', value: formatKgValue(summary.closing_inventory_kg), unit: 'kg' },
    {
      key: 'approved_adjustment_kg',
      value: formatKgValue(summaryApprovedAdjustmentKg(summary)),
      unit: 'kg',
    },
    {
      key: 'difference_kg',
      value: formatKgValue(summary.difference_kg ?? null),
      unit: localizedStatus(t, 'mass_balance_status', summary.status),
    },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-efs-text-muted">
            {t('efs.reports.monthly.kpis.confirmed_trips')}
          </p>
          <p
            className="mt-1 font-mono text-2xl font-semibold tabular-nums text-efs-text-primary"
            aria-label={`${t('efs.reports.monthly.kpis.confirmed_trips')} ${confirmedTrips ?? '-'}`}
          >
            {confirmedTrips ?? '-'}
          </p>
        </CardContent>
      </Card>
      {weightKpis.map((kpi) => (
        <Card key={kpi.key}>
          <CardContent className="p-4">
            <p className="text-xs text-efs-text-muted">
              {t(`efs.reports.monthly.kpis.${kpi.key}`)}
            </p>
            <p
              className="mt-1 font-mono text-2xl font-semibold tabular-nums text-efs-text-primary"
              aria-label={`${t(`efs.reports.monthly.kpis.${kpi.key}`)} ${kpi.value} ${kpi.unit}`}
            >
              {kpi.value} {kpi.unit}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const OverviewTab = ({ report }: { report: OperationalPreview }) => {
  const { t } = useTranslation(NS);

  const inboundColumns: MasterTableColumn<InboundCategorySummary>[] = [
    {
      key: 'incoming_item_name',
      header: t('efs.reports.monthly.external.inbound.item'),
      cell: (r) => r.incoming_item_name,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'package_count',
      header: t('efs.reports.monthly.external.inbound.packages'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.package_count}</span>,
    },
    {
      key: 'net_kg',
      header: t('efs.reports.monthly.external.inbound.net_kg'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.net_kg)}</span>,
    },
  ];

  const producedRows = report.dismantling_output_summary ?? [];
  const shipmentRows = report.final_material_shipment_summary ?? [];
  const destinationRows = report.final_destinations ?? [];

  const producedColumns: MasterTableColumn<DismantlingOutputSummary>[] = [
    {
      key: 'material_name',
      header: t('efs.reports.monthly.external.produced.material'),
      cell: (r) => r.material_name,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'produced_kg',
      header: t('efs.reports.monthly.external.produced.total_kg'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.produced_kg)}</span>,
    },
  ];

  const outboundColumns: MasterTableColumn<OutboundDestinationSummary>[] = [
    {
      key: 'company_name',
      header: t('efs.reports.monthly.external.outbound.company'),
      cell: (r) => r.company_name,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'dispatch_date',
      header: t('efs.reports.monthly.external.outbound.dispatch_date'),
      cell: (r) => r.dispatch_date,
    },
    {
      key: 'item_name',
      header: t('efs.reports.monthly.external.outbound.item'),
      cell: (r) => r.item_name,
    },
    {
      key: 'kg',
      header: t('efs.reports.monthly.external.outbound.kg'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.kg)}</span>,
    },
    {
      key: 'receipt_status',
      header: t('efs.reports.monthly.external.outbound.receipt_status'),
      cell: (r) => localizedStatus(t, 'receipt_status', r.receipt_status),
    },
    {
      key: 'resource_chain_status',
      header: t('efs.reports.monthly.external.outbound.resource_chain_status'),
      cell: (r) => localizedStatus(t, 'resource_chain_status', r.resource_chain_status),
    },
  ];

  const destinationColumns: MasterTableColumn<DestinationSummary>[] = [
    {
      key: 'company_name',
      header: t('efs.reports.monthly.external.destination.company'),
      cell: (r) => r.company_name,
    },
    {
      key: 'destination_type',
      header: t('efs.reports.monthly.external.destination.type'),
      cell: (r) => (
        <Badge variant="outline">
          {t(`efs.reports.monthly.external.destination.types.${r.destination_type}`, {
            defaultValue: r.destination_type,
          })}
        </Badge>
      ),
    },
    {
      key: 'shipment_count',
      header: t('efs.reports.monthly.external.destination.shipments'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.shipment_count}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <KpiGrid report={report} />
      <BasicInfoCard info={report.basic_info} />
      {report.source_updated_at ? (
        <Alert>
          <AlertTitle>{t('efs.reports.monthly.external.source.title')}</AlertTitle>
          <AlertDescription>
            {t('efs.reports.monthly.external.source.updated_at', {
              value: formatDateTime(report.source_updated_at),
            })}
          </AlertDescription>
        </Alert>
      ) : null}
      <SectionCard title={t('efs.reports.monthly.external.inbound.title')}>
        <MasterTable
          columns={inboundColumns}
          rows={report.inbound_category_inspection}
          rowKey={(r) => r.incoming_item_id}
        />
      </SectionCard>
      <SectionCard title={t('efs.reports.monthly.external.produced.title')}>
        <MasterTable columns={producedColumns} rows={producedRows} rowKey={(r) => r.material_id} />
      </SectionCard>
      {shipmentRows.length ? (
        <SectionCard title={t('efs.reports.monthly.external.shipment.title')}>
          <MasterTable
            columns={[
              {
                key: 'material_name',
                header: t('efs.reports.monthly.external.shipment.material'),
                cell: (r: MaterialShipmentSummary) => r.material_name,
                className: 'sticky left-0 bg-efs-bg-panel',
              },
              {
                key: 'total_kg',
                header: t('efs.reports.monthly.external.shipment.total_kg'),
                align: 'right',
                cell: (r) => (
                  <span className="font-mono tabular-nums">{formatKgValue(r.total_kg)}</span>
                ),
              },
            ]}
            rows={shipmentRows}
            rowKey={(r) => r.material_id}
          />
        </SectionCard>
      ) : null}
      <SectionCard title={t('efs.reports.monthly.external.outbound.title')}>
        <MasterTable
          columns={outboundColumns}
          rows={report.outbound_destination_summary}
          rowKey={(r) => r.outbound_line_id}
        />
      </SectionCard>
      {destinationRows.length ? (
        <SectionCard title={t('efs.reports.monthly.external.destination.title')}>
          <MasterTable
            columns={destinationColumns}
            rows={destinationRows}
            rowKey={(r) => r.destination_company_id}
          />
        </SectionCard>
      ) : null}
      {'notes' in report ? (
        <SectionCard title={t('efs.reports.monthly.external.notes.title')}>
          <p className="whitespace-pre-wrap text-sm text-efs-text-primary">
            {report.notes?.trim() ? report.notes : t('efs.reports.monthly.external.notes.empty')}
          </p>
        </SectionCard>
      ) : null}
    </div>
  );
};

const SettlementProjectionPanel = ({
  title,
  projection,
}: {
  title: string;
  projection: SettlementProjectionDto;
}) => {
  const { t } = useTranslation(NS);
  const lineColumns: MasterTableColumn<FinancialLineDto>[] = [
    {
      key: 'received_on',
      header: t('efs.reports.monthly.financial_projection.received_on'),
      cell: (r) => r.received_on,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'lot_code',
      header: t('efs.reports.monthly.financial_projection.lot_code'),
      cell: (r) => r.lot_code,
    },
    {
      key: 'item_name',
      header: t('efs.reports.monthly.financial_projection.item_name'),
      cell: (r) => r.item_name,
    },
    {
      key: 'quantity',
      header: t('efs.reports.monthly.financial_projection.quantity'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.quantity)}</span>,
    },
    {
      key: 'unit',
      header: t('efs.reports.monthly.financial_projection.unit'),
      cell: (r) => r.unit,
    },
    {
      key: 'unit_price',
      header: t('efs.reports.monthly.financial_projection.unit_price'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.unit_price)}</span>,
    },
    {
      key: 'amount',
      header: t('efs.reports.monthly.financial_projection.amount'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.amount)}</span>,
    },
  ];
  const feeColumns: MasterTableColumn<FinancialFeeDto>[] = [
    {
      key: 'fee_code',
      header: t('efs.reports.monthly.financial_projection.fee_code'),
      cell: (r) => r.fee_code,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'fee_category',
      header: t('efs.reports.monthly.financial_projection.fee_category'),
      cell: (r) => r.fee_category,
    },
    {
      key: 'quantity',
      header: t('efs.reports.monthly.financial_projection.quantity'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.quantity)}</span>,
    },
    {
      key: 'unit',
      header: t('efs.reports.monthly.financial_projection.unit'),
      cell: (r) => r.unit,
    },
    {
      key: 'unit_price',
      header: t('efs.reports.monthly.financial_projection.unit_price'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.unit_price)}</span>,
    },
    {
      key: 'amount',
      header: t('efs.reports.monthly.financial_projection.amount'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.amount)}</span>,
    },
    {
      key: 'taxable',
      header: t('efs.reports.monthly.financial_projection.taxable'),
      cell: (r) =>
        r.taxable
          ? t('efs.reports.monthly.financial_projection.yes')
          : t('efs.reports.monthly.financial_projection.no'),
    },
  ];
  const totals = [
    'inbound_total_kg',
    'subtotal',
    'taxable_subtotal',
    'tax_rate',
    'tax_amount',
    'total',
  ] as const;
  return (
    <SectionCard title={title}>
      <div className="mb-4 grid gap-x-8 gap-y-1 md:grid-cols-2">
        <div className="border-b border-efs-border py-1.5 text-sm">
          <span className="text-efs-text-muted">
            {t('efs.reports.monthly.financial_projection.recipient')}
          </span>
          <span className="ml-2 font-medium">{projection.recipient_name}</span>
        </div>
        <div className="border-b border-efs-border py-1.5 text-sm">
          <span className="text-efs-text-muted">
            {t('efs.reports.monthly.financial_projection.term_version')}
          </span>
          <span className="ml-2 font-mono">v{projection.term_version}</span>
        </div>
        {totals.map((key) => (
          <div
            key={key}
            className="flex justify-between gap-4 border-b border-efs-border py-1.5 text-sm"
          >
            <span className="text-efs-text-muted">
              {t(`efs.reports.monthly.financial_projection.${key}`)}
            </span>
            <span className="font-mono tabular-nums">
              {key === 'inbound_total_kg'
                ? formatKgValue(projection[key])
                : formatAmount(projection[key])}
            </span>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        <MasterTable
          columns={lineColumns}
          rows={projection.lines}
          rowKey={(r) => `${r.received_on}-${r.lot_code}-${r.item_name}`}
        />
        <MasterTable columns={feeColumns} rows={projection.fees} rowKey={(r) => r.fee_code} />
      </div>
    </SectionCard>
  );
};

const EstimateProjectionPanel = ({ projection }: { projection: EstimateProjectionDto }) => {
  const { t } = useTranslation(NS);
  const totals = [
    'material_recovery_value',
    'first_valuation_value',
    'freight_value',
    'distributable_value',
    'processor_share',
    'platform_share',
    'platform_to_processor_settlement',
    'actual_sales_amount',
    'estimate_vs_actual_difference',
  ] as const;
  return (
    <SectionCard title={t('efs.reports.monthly.financial_projection.internal_estimate_title')}>
      <div className="grid gap-x-8 gap-y-1 md:grid-cols-2">
        {totals.map((key) => (
          <div
            key={key}
            className="flex justify-between gap-4 border-b border-efs-border py-1.5 text-sm"
          >
            <span className="text-efs-text-muted">
              {t(`efs.reports.monthly.financial_projection.${key}`)}
            </span>
            <span className="font-mono tabular-nums">{formatAmount(projection[key])}</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const SettlementTab = ({ precheck }: { precheck?: MonthlyReportPrecheckDto }) => {
  const { t } = useTranslation(NS);
  const settlement = precheck?.financial?.customer_settlement;
  return (
    <div className="space-y-4">
      {settlement ? (
        <SettlementProjectionPanel
          title={t('efs.reports.monthly.settlement.title')}
          projection={settlement}
        />
      ) : (
        <SectionCard title={t('efs.reports.monthly.settlement.title')}>
          <p className="mb-3 text-sm text-efs-text-muted">
            {t('efs.reports.monthly.settlement.description')}
          </p>
          <Alert>
            <Lock className="h-4 w-4" />
            <AlertTitle>{t('efs.reports.monthly.settlement.waiting_title')}</AlertTitle>
            <AlertDescription>
              {t('efs.reports.monthly.settlement.waiting_description')}
            </AlertDescription>
          </Alert>
        </SectionCard>
      )}
    </div>
  );
};

const InternalAnalysis = ({ report }: { report: InternalMonthlyAnalysisDto }) => {
  const { t } = useTranslation(NS);

  const financials: Array<{ key: string; value: string | null; emphasize?: boolean }> = [
    { key: 'value_a', value: report.value_a },
    { key: 'value_b', value: report.value_b },
    { key: 'value_c', value: report.value_c },
    { key: 'value_d', value: report.value_d, emphasize: true },
    { key: 'dk_share', value: report.dk_share },
    { key: 'ef_share', value: report.ef_share },
    { key: 'ef_to_dk_settlement_price', value: report.ef_to_dk_settlement_price, emphasize: true },
    { key: 'estimate_vs_actual_difference', value: report.estimate_vs_actual_difference },
    { key: 'dk_actual_sales_amount', value: report.dk_actual_sales_amount },
    { key: 'loss_rate', value: report.loss_rate },
  ];

  const unitPriceColumns: MasterTableColumn<MaterialUnitPrice>[] = [
    {
      key: 'material_name',
      header: t('efs.reports.monthly.internal.unit_prices.material'),
      cell: (r) => r.material_name,
    },
    {
      key: 'weight',
      header: t('efs.reports.monthly.internal.unit_prices.weight'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.weight)}</span>,
    },
    {
      key: 'unit_price',
      header: t('efs.reports.monthly.internal.unit_prices.unit_price'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.unit_price)}</span>,
    },
    {
      key: 'line_value',
      header: t('efs.reports.monthly.internal.unit_prices.line_value'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.line_value)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <SectionCard title={t('efs.reports.monthly.internal.financials.title')}>
        <dl className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
          {financials.map((f) => (
            <div
              key={f.key}
              className="flex items-baseline justify-between gap-4 border-b border-efs-border py-1.5"
            >
              <dt className="text-sm text-efs-text-muted">
                {t(`efs.reports.monthly.internal.${f.key}`)}
              </dt>
              <dd
                className={cn(
                  'font-mono tabular-nums',
                  f.emphasize ? 'text-lg font-semibold text-efs-text-primary' : 'text-sm',
                )}
              >
                {formatAmount(f.value)}
              </dd>
            </div>
          ))}
        </dl>
      </SectionCard>
      <SectionCard title={t('efs.reports.monthly.internal.unit_prices.title')}>
        <MasterTable
          columns={unitPriceColumns}
          rows={report.material_unit_prices}
          rowKey={(r) => r.material_id}
        />
      </SectionCard>
      <SectionCard title={t('efs.reports.monthly.internal.high_value.title')}>
        <dl className="mb-3 grid gap-x-8 gap-y-1 sm:grid-cols-3">
          <div className="border-b border-efs-border py-1.5">
            <dt className="text-sm text-efs-text-muted">
              {t('efs.reports.monthly.internal.high_value.recovered_kg')}
            </dt>
            <dd className="font-mono text-sm tabular-nums">
              {formatKgValue(report.high_value.high_value_output_kg)}
            </dd>
          </div>
          <div className="border-b border-efs-border py-1.5">
            <dt className="text-sm text-efs-text-muted">
              {t('efs.reports.monthly.internal.high_value.total_kg')}
            </dt>
            <dd className="font-mono text-sm tabular-nums">
              {formatKgValue(report.high_value.total_output_kg)}
            </dd>
          </div>
          <div className="border-b border-efs-border py-1.5">
            <dt className="text-sm text-efs-text-muted">
              {t('efs.reports.monthly.internal.high_value.recovery_rate')}
            </dt>
            <dd className="font-mono text-sm font-semibold tabular-nums">
              {report.high_value.recovery_rate == null
                ? '-'
                : `${(Number(report.high_value.recovery_rate) * 100).toFixed(1)}%`}
            </dd>
          </div>
        </dl>
      </SectionCard>
    </div>
  );
};

const ValuationComparison = ({ report }: { report: ValuationComparisonDto }) => {
  const { t } = useTranslation(NS);
  const columns: MasterTableColumn<ValuationComparisonLine>[] = [
    {
      key: 'material_name',
      header: t('efs.reports.monthly.comparison.material'),
      cell: (r) => r.material_name,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'post_weight',
      header: t('efs.reports.monthly.comparison.post_weight'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.post_weight)}</span>,
    },
    {
      key: 'post_line_value',
      header: t('efs.reports.monthly.comparison.post_value'),
      align: 'right',
      cell: (r) => (
        <span className="font-mono tabular-nums">{formatAmount(r.post_line_value)}</span>
      ),
    },
    {
      key: 'actual_weight',
      header: t('efs.reports.monthly.comparison.actual_weight'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatKgValue(r.actual_weight)}</span>,
    },
    {
      key: 'actual_amount',
      header: t('efs.reports.monthly.comparison.actual_amount'),
      align: 'right',
      cell: (r) => <span className="font-mono tabular-nums">{formatAmount(r.actual_amount)}</span>,
    },
    {
      key: 'actual_vs_post_diff',
      header: t('efs.reports.monthly.comparison.diff'),
      align: 'right',
      cell: (r) => (
        <span className="font-mono font-semibold tabular-nums">
          {formatAmount(r.actual_vs_post_diff)}
        </span>
      ),
    },
  ];

  return (
    <SectionCard title={t('efs.reports.monthly.comparison.title')}>
      <div className="mb-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
        {(
          [
            'pre_first_valuation_value',
            'post_material_valuation_value',
            'actual_sales_amount',
            'post_vs_pre_diff',
            'actual_vs_post_diff',
          ] as const
        ).map((key) => (
          <div
            key={key}
            className="flex justify-between gap-4 border-b border-efs-border py-1.5 text-sm"
          >
            <span className="text-efs-text-muted">
              {t(`efs.reports.monthly.comparison.summary.${key}`)}
            </span>
            <span className="font-mono tabular-nums">{formatAmount(report[key])}</span>
          </div>
        ))}
      </div>
      <MasterTable columns={columns} rows={report.lines} rowKey={(r) => r.material_id} />
    </SectionCard>
  );
};

const SettingsDraftPanel = ({
  batchId,
  variant,
  canManageProfiles,
  canManageTerms,
}: {
  batchId?: string;
  variant: MonthlyReportVariant;
  canManageProfiles: boolean;
  canManageTerms: boolean;
}) => {
  const { t } = useTranslation(NS);
  const [settingVariant, setSettingVariant] = useState<MonthlyReportVariant>(
    variant === 'internal_full' ? 'external_operations' : variant,
  );
  const [supersedeTarget, setSupersedeTarget] = useState<{
    kind: 'profile' | 'terms';
    id: string;
  } | null>(null);
  const [supersedeReason, setSupersedeReason] = useState('');
  const [profilePage, setProfilePage] = useState(1);
  const [profilePerPage, setProfilePerPage] = useState(50);
  const [termsPage, setTermsPage] = useState(1);
  const [termsPerPage, setTermsPerPage] = useState(50);
  const profilesQuery = useMonthlyReportProfiles(
    batchId,
    settingVariant,
    profilePage,
    profilePerPage,
    canManageProfiles,
  );
  const termsQuery = useMonthlyReportCommercialTerms(
    batchId,
    settingVariant,
    termsPage,
    termsPerPage,
    canManageTerms && MONEY_VARIANTS.has(settingVariant),
  );
  const lockProfile = useLockMonthlyReportProfile(batchId);
  const supersedeProfile = useSupersedeMonthlyReportProfile(batchId);
  const lockTerms = useLockMonthlyReportCommercialTerms(batchId);
  const supersedeTerms = useSupersedeMonthlyReportCommercialTerms(batchId);

  const profileColumns: MasterTableColumn<MonthlyReportProfileDto>[] = [
    {
      key: 'issuer_name',
      header: t('efs.reports.monthly.settings.columns.issuer'),
      cell: (r) => r.issuer_name,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'version',
      header: t('efs.reports.monthly.settings.columns.version'),
      align: 'right',
      cell: (r) => <span className="font-mono">v{r.version}</span>,
    },
    {
      key: 'status',
      header: t('efs.reports.monthly.settings.columns.status'),
      cell: (r) => (
        <Badge variant={settingStatusVariant(r.status)}>
          {t(`efs.reports.monthly.settings.status.${r.status}`, { defaultValue: r.status })}
        </Badge>
      ),
    },
    {
      key: 'locale',
      header: t('efs.reports.monthly.settings.columns.locale'),
      cell: (r) => r.output_locale,
    },
    {
      key: 'effective_from',
      header: t('efs.reports.monthly.settings.columns.effective'),
      cell: (r) => `${r.effective_from}${r.effective_to ? ` - ${r.effective_to}` : ''}`,
    },
    {
      key: 'updated_at',
      header: t('efs.reports.monthly.settings.columns.updated_at'),
      cell: (r) => formatDateTime(r.updated_at),
    },
    {
      key: 'actions',
      header: t('efs.reports.monthly.settings.columns.actions'),
      cell: (r) => (
        <div className="flex flex-wrap gap-2">
          {r.status === 'draft' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={lockProfile.isPending}
              onClick={() => lockProfile.mutate(r.monthly_report_profile_id)}
            >
              {t('efs.reports.monthly.settings.lock')}
            </Button>
          ) : null}
          {r.status !== 'superseded' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={supersedeProfile.isPending}
              onClick={() => {
                setSupersedeReason('');
                setSupersedeTarget({ kind: 'profile', id: r.monthly_report_profile_id });
              }}
            >
              {t('efs.reports.monthly.settings.supersede')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const termsColumns: MasterTableColumn<MonthlyReportCommercialTermsDto>[] = [
    {
      key: 'currency',
      header: t('efs.reports.monthly.settings.columns.currency'),
      cell: (r) => r.term_set.currency_code,
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'version',
      header: t('efs.reports.monthly.settings.columns.version'),
      align: 'right',
      cell: (r) => <span className="font-mono">v{r.term_set.version}</span>,
    },
    {
      key: 'status',
      header: t('efs.reports.monthly.settings.columns.status'),
      cell: (r) => (
        <Badge variant={settingStatusVariant(r.term_set.status)}>
          {t(`efs.reports.monthly.settings.status.${r.term_set.status}`, {
            defaultValue: r.term_set.status,
          })}
        </Badge>
      ),
    },
    {
      key: 'tax_rate',
      header: t('efs.reports.monthly.settings.columns.tax_rate'),
      align: 'right',
      cell: (r) => <span className="font-mono">{formatAmount(r.term_set.tax_rate)}</span>,
    },
    {
      key: 'rates',
      header: t('efs.reports.monthly.settings.columns.rates'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.incoming_item_rates.length}</span>,
    },
    {
      key: 'fees',
      header: t('efs.reports.monthly.settings.columns.fees'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.processing_fees.length}</span>,
    },
    {
      key: 'benchmarks',
      header: t('efs.reports.monthly.settings.columns.benchmarks'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.benchmark_quotes.length}</span>,
    },
    {
      key: 'updated_at',
      header: t('efs.reports.monthly.settings.columns.updated_at'),
      cell: (r) => formatDateTime(r.term_set.updated_at),
    },
    {
      key: 'actions',
      header: t('efs.reports.monthly.settings.columns.actions'),
      cell: (r) => (
        <div className="flex flex-wrap gap-2">
          {r.term_set.status === 'draft' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={lockTerms.isPending}
              onClick={() => lockTerms.mutate(r.term_set.monthly_report_term_set_id)}
            >
              {t('efs.reports.monthly.settings.lock')}
            </Button>
          ) : null}
          {r.term_set.status !== 'superseded' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={supersedeTerms.isPending}
              onClick={() => {
                setSupersedeReason('');
                setSupersedeTarget({ kind: 'terms', id: r.term_set.monthly_report_term_set_id });
              }}
            >
              {t('efs.reports.monthly.settings.supersede')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <SectionCard title={t('efs.reports.monthly.settings.title')}>
      <label className="mb-4 block text-sm">
        {t('efs.reports.monthly.settings.profile')}
        <select
          className="mt-1 min-h-10 w-full rounded-md border bg-background px-3"
          value={settingVariant}
          onChange={(event) => {
            setSettingVariant(event.target.value as MonthlyReportVariant);
            setProfilePage(1);
            setTermsPage(1);
          }}
        >
          {VARIANTS.filter((item) => item !== 'internal_full').map((item) => (
            <option key={item} value={item}>
              {t(`efs.reports.monthly.variants.${item}.name`)}
            </option>
          ))}
        </select>
      </label>
      {batchId ? (
        <ReportSettingsCreatePanel
          key={`${batchId}-${settingVariant}`}
          batchId={batchId}
          variant={settingVariant}
          canManageProfiles={canManageProfiles}
          canManageTerms={canManageTerms}
        />
      ) : null}
      <Alert className="mb-4">
        <Lock className="h-4 w-4" />
        <AlertTitle>{t('efs.reports.monthly.settings.lock_required_title')}</AlertTitle>
        <AlertDescription>
          {t('efs.reports.monthly.settings.lock_required_description')}
        </AlertDescription>
      </Alert>
      {!canManageProfiles && !canManageTerms ? (
        <Alert>
          <AlertTitle>{t('efs.reports.monthly.settings.no_permission_title')}</AlertTitle>
          <AlertDescription>
            {t('efs.reports.monthly.settings.no_permission_description')}
          </AlertDescription>
        </Alert>
      ) : null}
      {canManageProfiles ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-efs-text-primary">
            {t('efs.reports.monthly.settings.profile')}
          </h3>
          {profilesQuery.error ? <ErrorAlert error={profilesQuery.error} /> : null}
          <MasterTable
            columns={profileColumns}
            rows={profilesQuery.data?.items ?? []}
            rowKey={(r) => r.monthly_report_profile_id}
            pagination={{
              page: profilesQuery.data?.page ?? profilePage,
              perPage: profilesQuery.data?.per_page ?? profilePerPage,
              total: profilesQuery.data?.total ?? 0,
              onPageChange: setProfilePage,
              onPerPageChange: (next) => {
                setProfilePerPage(next);
                setProfilePage(1);
              },
              pageSizeOptions: [50, 100],
            }}
          />
        </div>
      ) : null}
      {canManageTerms && MONEY_VARIANTS.has(settingVariant) ? (
        <div className="mt-6 space-y-2">
          <h3 className="text-sm font-semibold text-efs-text-primary">
            {t('efs.reports.monthly.settings.terms')}
          </h3>
          {termsQuery.error ? <ErrorAlert error={termsQuery.error} /> : null}
          <MasterTable
            columns={termsColumns}
            rows={termsQuery.data?.items ?? []}
            rowKey={(r) => r.term_set.monthly_report_term_set_id}
            pagination={{
              page: termsQuery.data?.page ?? termsPage,
              perPage: termsQuery.data?.per_page ?? termsPerPage,
              total: termsQuery.data?.total ?? 0,
              onPageChange: setTermsPage,
              onPerPageChange: (next) => {
                setTermsPerPage(next);
                setTermsPage(1);
              },
              pageSizeOptions: [50, 100],
            }}
          />
        </div>
      ) : null}
      {[lockProfile.error, lockTerms.error, supersedeProfile.error, supersedeTerms.error]
        .filter(Boolean)
        .map((error, index) => (
          <ErrorAlert key={index} error={error} />
        ))}
      <Dialog
        open={supersedeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setSupersedeTarget(null);
        }}
      >
        <DialogContent>
          <DialogTitle>{t('efs.reports.monthly.settings.supersede')}</DialogTitle>
          <DialogDescription>
            {t('efs.reports.monthly.settings.supersede_reason')}
          </DialogDescription>
          <Textarea
            aria-label={t('efs.reports.monthly.settings.supersede_reason')}
            value={supersedeReason}
            onChange={(event) => setSupersedeReason(event.target.value)}
            maxLength={2000}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupersedeTarget(null)}>
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              disabled={supersedeProfile.isPending || supersedeTerms.isPending}
              onClick={() => {
                if (!supersedeTarget) return;
                const options = { onSuccess: () => setSupersedeTarget(null) };
                if (supersedeTarget.kind === 'profile')
                  supersedeProfile.mutate(
                    { profileId: supersedeTarget.id, reason: supersedeReason.trim() || null },
                    options,
                  );
                else
                  supersedeTerms.mutate(
                    { termSetId: supersedeTarget.id, reason: supersedeReason.trim() || null },
                    options,
                  );
              }}
            >
              {t('efs.reports.monthly.settings.supersede')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

const FinancialTab = ({
  internal,
  comparison,
  precheck,
}: {
  internal?: InternalMonthlyAnalysisDto;
  comparison?: ValuationComparisonDto;
  precheck?: MonthlyReportPrecheckDto;
}) => {
  const { t } = useTranslation(NS);
  const processorInvoice = precheck?.financial?.processor_invoice;
  const internalEstimate = precheck?.financial?.internal_estimate;
  return (
    <div className="space-y-4">
      {processorInvoice ? (
        <SettlementProjectionPanel
          title={t('efs.reports.monthly.financial_projection.processor_invoice_title')}
          projection={processorInvoice}
        />
      ) : null}
      {internalEstimate ? <EstimateProjectionPanel projection={internalEstimate} /> : null}
      {internal ? (
        <InternalAnalysis report={internal} />
      ) : (
        <Alert>
          <AlertTitle>{t('efs.reports.monthly.financial.no_internal_title')}</AlertTitle>
          <AlertDescription>
            {t('efs.reports.monthly.financial.no_internal_description')}
          </AlertDescription>
        </Alert>
      )}
      {comparison ? <ValuationComparison report={comparison} /> : null}
    </div>
  );
};

const IssuesTab = ({
  report,
  precheck,
}: {
  report?: ExternalMonthlyReportDto;
  precheck?: MonthlyReportPrecheckDto;
}) => {
  const { t } = useTranslation(NS);
  const legacyIssues = report?.abnormal_summary.items ?? [];
  return (
    <div className="space-y-4">
      {precheck?.issues.length ? (
        precheck.issues.map((issue, index) => (
          <AuditIssueCard key={issueKey(issue, index)} presentation={issue} />
        ))
      ) : legacyIssues.length ? (
        <SectionCard title={t('efs.reports.monthly.external.abnormal.title')}>
          <MasterTable
            columns={[
              {
                key: 'exception_code',
                header: t('efs.reports.monthly.external.abnormal.code'),
                cell: (r: AbnormalItem) =>
                  isCatalogedIssueCode(r.exception_code)
                    ? t(issueCodeKeys(r.exception_code).title, { ns: 'issues' })
                    : t('efs.issues.generic.title', { ns: 'issues' }),
              },
              {
                key: 'severity',
                header: t('efs.reports.monthly.external.abnormal.severity'),
                cell: (r) => r.severity,
              },
              {
                key: 'status',
                header: t('efs.reports.monthly.external.abnormal.status'),
                cell: (r) => r.status,
              },
              {
                key: 'description',
                header: t('efs.reports.monthly.external.abnormal.description'),
                cell: (r) => r.description,
              },
            ]}
            rows={legacyIssues}
            rowKey={(r) => r.exception_id}
          />
        </SectionCard>
      ) : (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>{t('efs.reports.monthly.issues.empty_title')}</AlertTitle>
          <AlertDescription>{t('efs.reports.monthly.issues.empty_description')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

const VariantCard = ({
  variant,
  precheck,
  latest,
  canView,
  canGenerate,
  isSelected,
  onSelect,
  onGenerate,
  isGenerating,
  downloadingArtifactId,
  downloadError,
  onDownload,
}: {
  variant: MonthlyReportVariant;
  precheck?: MonthlyReportPrecheckDto;
  latest?: MonthlyReportArtifactDto | null;
  canView: boolean;
  canGenerate: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  downloadingArtifactId: string | null;
  downloadError: unknown;
  onDownload: (artifact: MonthlyReportArtifactDto) => void;
}) => {
  const { t } = useTranslation(NS);
  const containsMoney = MONEY_VARIANTS.has(variant);
  const artifact = latest ?? precheck?.latest_artifact ?? null;
  return (
    <div
      className={cn(
        'rounded-md border p-3',
        isSelected ? 'border-efs-accent bg-efs-bg-subtle' : 'border-efs-border bg-efs-bg-panel',
      )}
    >
      <button type="button" onClick={onSelect} className="w-full text-left">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium text-efs-text-primary">
              {t(`efs.reports.monthly.variants.${variant}.name`)}
            </p>
            <p className="mt-0.5 text-xs text-efs-text-muted">
              {t(`efs.reports.monthly.variants.${variant}.audience`)}
            </p>
          </div>
          {containsMoney ? (
            <Badge variant="destructive">
              <ShieldAlert className="mr-1 h-3 w-3" />
              {t('efs.reports.monthly.variant.money')}
            </Badge>
          ) : (
            <Badge variant="outline">{t('efs.reports.monthly.variant.no_money')}</Badge>
          )}
        </div>
      </button>
      <div className="mt-3 space-y-1 text-xs text-efs-text-muted">
        <p>
          {t('efs.reports.monthly.variant.permission')}: {viewPermissionFor(variant)}
        </p>
        <p>
          {t('efs.reports.monthly.variant.latest')}:{' '}
          {artifact
            ? `v${artifact.version} / ${artifact.stale ? t('efs.reports.monthly.artifacts.stale') : t(`efs.reports.monthly.artifacts.status.${artifact.status}`)}`
            : t('efs.reports.monthly.variant.none')}
        </p>
        <p>
          {t('efs.reports.monthly.variant.fingerprint')}:{' '}
          {shortHash(precheck?.source_fingerprint ?? artifact?.source_fingerprint)}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {artifact?.status === 'ready' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={downloadingArtifactId === artifact.artifact_id}
            onClick={() => onDownload(artifact)}
          >
            {downloadingArtifactId === artifact.artifact_id ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Download className="mr-1 h-3 w-3" />
            )}
            {t('efs.reports.monthly.actions.download')}
          </Button>
        ) : null}
        <Button
          size="sm"
          disabled={!canView || !canGenerate || !precheck?.can_generate || isGenerating}
          onClick={onGenerate}
        >
          {isGenerating ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-1 h-3 w-3" />
          )}
          {t('efs.reports.monthly.actions.generate')}
        </Button>
      </div>
      {artifact?.stale ? (
        <p className="mt-2 text-xs text-destructive">
          {t('efs.reports.monthly.artifacts.stale_notice')}
        </p>
      ) : null}
      {downloadError ? <LocalizedApiError error={downloadError} /> : null}
    </div>
  );
};

/**
 * Short preview of a precheck issue in the readiness rail: title plus the
 * record it points at. The full card (next action, open-target button) lives
 * on the issues tab. Long titles and codes wrap rather than truncate (spec-13
 * §7.1 forbids tooltip-only text); lines of one issue sit close together and
 * issues are spaced apart so a wrapped title still reads as one item.
 */
const ReadinessIssueLine = ({ issue }: { issue: IssuePresentation }) => {
  const { t } = useTranslation(['issues']);
  const { title } = useIssueText(issue);
  const record = useTargetRecord(issue.target);
  // The whole page is one monthly batch, so naming it again adds nothing.
  const where =
    record && record.recordType !== 'monthly_batch'
      ? `${record.label}: ${record.code ?? t('efs.issues.target.code_unavailable')}`
      : null;
  // Spans, not <p>: AlertDescription gives paragraphs a loose line height.
  return (
    <li className="min-w-0">
      <span className="block break-words font-medium leading-snug">{title}</span>
      {where ? <span className="block break-all text-xs leading-snug">{where}</span> : null}
    </li>
  );
};

const ReadinessRail = ({
  selectedVariant,
  onVariantChange,
  precheck,
  precheckError,
  canView,
  canGenerate,
  onGenerate,
  isGenerating,
  downloadingArtifactId,
  downloadError,
  onDownload,
  onShowIssues,
}: {
  selectedVariant: MonthlyReportVariant;
  onVariantChange: (variant: MonthlyReportVariant) => void;
  precheck?: MonthlyReportPrecheckDto;
  precheckError: ApiClientError | null;
  canView: (variant: MonthlyReportVariant) => boolean;
  canGenerate: (variant: MonthlyReportVariant) => boolean;
  onGenerate: () => void;
  isGenerating: boolean;
  downloadingArtifactId: string | null;
  downloadError: unknown;
  onDownload: (artifact: MonthlyReportArtifactDto) => void;
  /** Opens the issues tab; omitted when the viewer cannot see that tab. */
  onShowIssues?: () => void;
}) => {
  const { t } = useTranslation(NS);
  const hiddenIssueCount = precheck
    ? Math.max(precheck.issues.length - READINESS_ISSUE_PREVIEW, 0)
    : 0;
  return (
    <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('efs.reports.monthly.readiness.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {precheckError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('efs.reports.monthly.readiness.failed_title')}</AlertTitle>
              <LocalizedApiError error={precheckError} />
            </Alert>
          ) : precheck ? (
            <Alert variant={precheck.can_generate ? 'default' : 'destructive'}>
              {precheck.can_generate ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertTitle>
                {precheck.can_generate
                  ? t('efs.reports.monthly.readiness.ready')
                  : t('efs.reports.monthly.readiness.blocked')}
              </AlertTitle>
              <AlertDescription>
                {t('efs.reports.monthly.readiness.issue_count', { count: precheck.issues.length })}
                {precheck.issues.length ? (
                  <>
                    <ul
                      className="mt-2 space-y-2"
                      aria-label={t('efs.reports.monthly.readiness.issue_list')}
                    >
                      {precheck.issues.slice(0, READINESS_ISSUE_PREVIEW).map((issue, index) => (
                        <ReadinessIssueLine key={issueKey(issue, index)} issue={issue} />
                      ))}
                    </ul>
                    {hiddenIssueCount ? (
                      <p className="mt-1.5 text-xs">
                        {t('efs.reports.monthly.readiness.issue_more', {
                          count: hiddenIssueCount,
                        })}
                      </p>
                    ) : null}
                    {onShowIssues ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={onShowIssues}
                      >
                        {t('efs.reports.monthly.readiness.show_issues')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-center gap-2 text-sm text-efs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('efs.reports.monthly.readiness.checking')}
            </div>
          )}
          <div className="space-y-2">
            {VARIANTS.map((variant) => (
              <VariantCard
                key={variant}
                variant={variant}
                precheck={variant === selectedVariant ? precheck : undefined}
                canView={canView(variant)}
                canGenerate={canGenerate(variant)}
                isSelected={variant === selectedVariant}
                onSelect={() => onVariantChange(variant)}
                onGenerate={onGenerate}
                isGenerating={isGenerating && variant === selectedVariant}
                downloadingArtifactId={downloadingArtifactId}
                downloadError={variant === selectedVariant ? downloadError : null}
                onDownload={onDownload}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </aside>
  );
};

const HistoryTab = ({
  artifacts,
  total,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  downloadingArtifactId,
  onDownload,
}: {
  artifacts: MonthlyReportArtifactDto[];
  total: number;
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  downloadingArtifactId: string | null;
  onDownload: (artifact: MonthlyReportArtifactDto) => void;
}) => {
  const { t } = useTranslation(NS);
  const columns: MasterTableColumn<MonthlyReportArtifactDto>[] = [
    {
      key: 'variant',
      header: t('efs.reports.monthly.artifacts.variant'),
      cell: (r) => t(`efs.reports.monthly.variants.${r.variant}.name`),
      className: 'sticky left-0 bg-efs-bg-panel',
    },
    {
      key: 'version',
      header: t('efs.reports.monthly.artifacts.version'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">v{r.version}</span>,
    },
    {
      key: 'output_locale',
      header: t('efs.reports.monthly.artifacts.locale'),
      cell: (r) => r.output_locale,
    },
    {
      key: 'generated_at',
      header: t('efs.reports.monthly.artifacts.generated_at'),
      cell: (r) => formatDateTime(r.generated_at),
    },
    {
      key: 'generated_by',
      header: t('efs.reports.monthly.artifacts.generated_by'),
      cell: (r) => r.generated_by ?? '-',
    },
    {
      key: 'size_bytes',
      header: t('efs.reports.monthly.artifacts.size'),
      align: 'right',
      cell: (r) => formatBytes(r.size_bytes),
    },
    {
      key: 'file_sha256',
      header: t('efs.reports.monthly.artifacts.hash'),
      cell: (r) => <code>{shortHash(r.file_sha256)}</code>,
    },
    {
      key: 'status',
      header: t('efs.reports.monthly.artifacts.status_label'),
      cell: (r) => (
        <Badge variant={r.stale ? 'destructive' : 'outline'}>
          {r.stale
            ? t('efs.reports.monthly.artifacts.stale')
            : t(`efs.reports.monthly.artifacts.status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'download',
      header: t('efs.reports.monthly.actions.download'),
      cell: (r) =>
        r.status === 'ready' ? (
          <div className="space-y-1">
            {r.stale ? (
              <p className="text-xs text-destructive">
                {t('efs.reports.monthly.artifacts.stale_notice')}
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={downloadingArtifactId === r.artifact_id}
              onClick={() => onDownload(r)}
            >
              {downloadingArtifactId === r.artifact_id ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : null}
              {t('efs.reports.monthly.actions.download')}
            </Button>
          </div>
        ) : (
          '-'
        ),
    },
  ];
  return (
    <SectionCard title={t('efs.reports.monthly.history.title')}>
      <MasterTable
        columns={columns}
        rows={artifacts}
        rowKey={(r) => r.artifact_id}
        pagination={{
          page,
          perPage,
          total,
          onPageChange,
          onPerPageChange,
          pageSizeOptions: [50, 100],
        }}
      />
    </SectionCard>
  );
};

export const MonthlyReportsPage = () => {
  const { t, i18n } = useTranslation(NS);
  const { batchId } = useParams();
  const user = useCurrentUser();
  const [selectedTab, setTab] = useState<ReportTab>('overview');
  const [selectedVariant, setVariant] = useState<MonthlyReportVariant>('external_operations');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage, setHistoryPerPage] = useState(50);
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const [recipientCompanyId, setRecipientCompanyId] = useState<string | null>(null);
  const tablistRef = useRef<HTMLDivElement>(null);

  const locale = (['ja', 'zh-TW', 'en'].includes(i18n.language) ? i18n.language : 'ja') as
    | 'ja'
    | 'zh-TW'
    | 'en';
  const canViewVariant = (v: MonthlyReportVariant) => hasPermission(user, viewPermissionFor(v));
  const canGenerateVariant = (v: MonthlyReportVariant) =>
    hasPermission(user, generatePermissionFor(v));
  const canViewExternal = canViewVariant('external_operations');
  const canViewInternal = hasPermission(user, 'report.internal_analysis.view');
  const anyReportAccess = VARIANTS.some(canViewVariant);
  const variant = canViewVariant(selectedVariant)
    ? selectedVariant
    : (VARIANTS.find(canViewVariant) ?? 'external_operations');
  const tab =
    selectedTab === 'overview' && !canViewExternal
      ? canViewVariant('customer_settlement')
        ? 'settlement'
        : 'financial'
      : selectedTab;
  const isReportSettingsParty =
    hasPermission(user, 'system.admin') || user?.partyType === 'platform';
  const canManageProfiles = isReportSettingsParty && hasPermission(user, 'settings.manage');
  const canManageTerms = isReportSettingsParty && hasPermission(user, 'pricing.manage');

  useEffect(() => {
    setRecipientCompanyId(null);
    setHistoryPage(1);
    setSettingsOpen(false);
  }, [batchId]);

  const externalQuery = useExternalMonthlyReport(batchId, canViewExternal);
  const internalQuery = useInternalMonthlyAnalysis(batchId, canViewInternal);
  const comparisonQuery = useValuationComparison(batchId, canViewInternal);
  const precheckQuery = useMonthlyReportPrecheck(
    batchId,
    variant,
    locale,
    recipientCompanyId,
    canViewVariant(variant),
  );
  const artifactsQuery = useMonthlyReportArtifacts(
    batchId,
    variant,
    historyPage,
    historyPerPage,
    recipientCompanyId,
    canViewVariant(variant),
  );
  const generateMutation = useGenerateMonthlyReportArtifact(batchId);

  const artifacts = artifactsQuery.data?.items ?? [];
  const latestArtifact =
    precheckQuery.data?.latest_artifact ??
    artifacts.find((artifact) => artifact.status === 'ready') ??
    null;
  const stale = !!latestArtifact?.stale;

  const handleGenerate = () => {
    if (!precheckQuery.data || !batchId) return;
    generateMutation.mutate({
      variant,
      output_locale: locale,
      expected_source_fingerprint: precheckQuery.data.source_fingerprint,
      idempotency_key: newIdempotencyKey(),
      recipient_company_id: recipientCompanyId,
    });
  };

  const handleDownload = async (artifact: MonthlyReportArtifactDto) => {
    setDownloadError(null);
    setDownloadingArtifactId(artifact.artifact_id);
    try {
      await downloadMonthlyReportArtifact(artifact.artifact_id, artifactFileName(artifact));
    } catch (error) {
      setDownloadError(error);
    } finally {
      setDownloadingArtifactId(null);
    }
  };

  const tabs: Array<{ key: ReportTab; label: string; enabled: boolean }> = [
    { key: 'overview', label: t('efs.reports.monthly.tabs.overview'), enabled: canViewExternal },
    {
      key: 'settlement',
      label: t('efs.reports.monthly.tabs.settlement'),
      enabled: canViewVariant('customer_settlement') || canViewVariant('external_operations'),
    },
    {
      key: 'financial',
      label: t('efs.reports.monthly.tabs.financial'),
      enabled: canViewInternal || canViewVariant('processor_invoice'),
    },
    { key: 'issues', label: t('efs.reports.monthly.tabs.issues'), enabled: anyReportAccess },
    { key: 'history', label: t('efs.reports.monthly.tabs.history'), enabled: anyReportAccess },
  ];

  const report = externalQuery.data;
  const operationalReport: ExternalMonthlyReportSnapshotDto | ExternalMonthlyReportDto | undefined =
    precheckQuery.data?.operational ?? report;
  const recipientChoices = precheckQuery.data?.operational?.recipient_choices ?? [];

  const switchTab = (next: ReportTab) => {
    setTab(next);
    if (next === 'settlement') {
      setVariant(
        canViewVariant('customer_settlement') ? 'customer_settlement' : 'external_operations',
      );
      setHistoryPage(1);
    } else if (next === 'financial' && variant === 'external_operations') {
      setVariant(
        canViewInternal
          ? 'internal_estimate'
          : canViewVariant('processor_invoice')
            ? 'processor_invoice'
            : 'customer_settlement',
      );
      setHistoryPage(1);
    }
  };

  // The readiness rail sits beside the tab content on desktop but below it on
  // narrow screens, so bring the tabs back into view after switching.
  // `block: 'nearest'` scrolls only when the tabs are off screen (narrow
  // screens), leaving a desktop page where they are already visible in place.
  // The tab strip also scrolls sideways on narrow screens; `inline: 'nearest'`
  // keeps the issues tab itself visible instead of cut off at the right edge.
  // Focus follows to the issues tab so keyboard users continue from there
  // instead of from the rail below the content (spec-17: tab switching and
  // issue navigation must be keyboard-completable).
  const showIssues = () => {
    switchTab('issues');
    const issuesTab = tablistRef.current?.querySelector<HTMLElement>('[data-tab="issues"]');
    issuesTab?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    issuesTab?.focus({ preventScroll: true });
  };

  return (
    <div className="space-y-4 pb-24">
      <PageHeader
        title={t('efs.reports.monthly.title')}
        subtitle={t('efs.reports.monthly.subtitle')}
      />

      {!anyReportAccess ? (
        <Alert>
          <AlertTitle>{t('efs.reports.monthly.no_access.title')}</AlertTitle>
          <AlertDescription>{t('efs.reports.monthly.no_access.description')}</AlertDescription>
        </Alert>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-5">
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.facility')}
                </p>
                <p className="font-medium text-efs-text-primary">
                  {operationalReport?.basic_info.facility_name ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.month')}
                </p>
                <p className="font-medium text-efs-text-primary">
                  {operationalReport?.basic_info.year_month ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.status')}
                </p>
                {operationalReport ? (
                  <BatchStatusBadge status={operationalReport.basic_info.status as BatchStatus} />
                ) : (
                  '-'
                )}
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.latest_artifact')}
                </p>
                <p className="font-medium text-efs-text-primary">
                  {latestArtifact
                    ? `v${latestArtifact.version} ${formatDateTime(latestArtifact.generated_at)}`
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.source_state')}
                </p>
                <p
                  className={cn(
                    'font-medium',
                    stale ? 'text-destructive' : 'text-efs-text-primary',
                  )}
                >
                  {stale
                    ? t('efs.reports.monthly.header.stale')
                    : t('efs.reports.monthly.header.current')}
                </p>
              </div>
              <div className="xl:col-span-5">
                <p className="text-xs text-efs-text-muted">
                  {t('efs.reports.monthly.header.source_hash')}
                </p>
                <p className="font-mono text-sm text-efs-text-primary">
                  {shortHash(
                    precheckQuery.data?.operational?.source_data_hash ??
                      precheckQuery.data?.source_fingerprint,
                  )}
                </p>
              </div>
              {recipientChoices.length > 1 ? (
                <label className="xl:col-span-5 block text-sm">
                  <span className="text-xs text-efs-text-muted">
                    {t('efs.reports.monthly.recipient.label')}
                  </span>
                  <select
                    className="mt-1 min-h-10 w-full rounded-md border border-efs-border bg-efs-bg-panel px-3 text-sm text-efs-text-primary"
                    value={recipientCompanyId ?? ''}
                    onChange={(event) => {
                      setRecipientCompanyId(event.target.value || null);
                      setHistoryPage(1);
                    }}
                    aria-label={t('efs.reports.monthly.recipient.label')}
                  >
                    <option value="">{t('efs.reports.monthly.recipient.placeholder')}</option>
                    {recipientChoices.map((choice) => (
                      <option key={choice.company_id} value={choice.company_id}>
                        {choice.display_name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : recipientChoices.length === 1 ? (
                <div className="xl:col-span-5">
                  <p className="text-xs text-efs-text-muted">
                    {t('efs.reports.monthly.recipient.label')}
                  </p>
                  <p className="text-sm font-medium text-efs-text-primary">
                    {recipientChoices[0].display_name}
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {canManageProfiles || canManageTerms ? (
            <div className="space-y-3">
              <Button
                variant="outline"
                aria-expanded={settingsOpen}
                onClick={() => setSettingsOpen((open) => !open)}
              >
                {t('efs.reports.monthly.settings.title')}
              </Button>
              {settingsOpen ? (
                <SettingsDraftPanel
                  key={batchId}
                  batchId={batchId}
                  variant={variant}
                  canManageProfiles={canManageProfiles}
                  canManageTerms={canManageTerms}
                />
              ) : null}
            </div>
          ) : null}

          <div
            ref={tablistRef}
            className="flex overflow-x-auto rounded-md border border-efs-border bg-efs-bg-panel p-0.5"
            role="tablist"
            aria-label={t('efs.reports.monthly.tabs.label')}
          >
            {tabs
              .filter((item) => item.enabled)
              .map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  data-tab={item.key}
                  aria-selected={tab === item.key}
                  onClick={() => switchTab(item.key)}
                  className={cn(
                    'min-h-10 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    tab === item.key
                      ? 'bg-efs-bg-subtle text-efs-text-primary'
                      : 'text-efs-text-muted hover:text-efs-text-primary',
                  )}
                >
                  {item.label}
                </button>
              ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(20rem,3fr)]">
            <main className="min-w-0 space-y-4">
              {generateMutation.error ? <ErrorAlert error={generateMutation.error} /> : null}
              {externalQuery.isLoading && tab !== 'financial' ? (
                <div className="flex items-center gap-2 py-12 text-sm text-efs-text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('efs.common.actions.loading', { ns: 'common' })}
                </div>
              ) : externalQuery.error && tab !== 'financial' ? (
                <ErrorAlert error={externalQuery.error} />
              ) : tab === 'overview' && operationalReport ? (
                <OverviewTab report={operationalReport} />
              ) : tab === 'settlement' ? (
                <SettlementTab precheck={precheckQuery.data} />
              ) : tab === 'financial' ? (
                <FinancialTab
                  internal={internalQuery.data}
                  comparison={comparisonQuery.data}
                  precheck={precheckQuery.data}
                />
              ) : tab === 'issues' ? (
                <IssuesTab report={report} precheck={precheckQuery.data} />
              ) : tab === 'history' ? (
                artifactsQuery.error ? (
                  <ErrorAlert
                    error={artifactsQuery.error}
                    title={t('efs.reports.monthly.history.failed_title')}
                  />
                ) : (
                  <HistoryTab
                    artifacts={artifacts}
                    total={artifactsQuery.data?.total ?? artifacts.length}
                    page={artifactsQuery.data?.page ?? historyPage}
                    perPage={artifactsQuery.data?.per_page ?? historyPerPage}
                    onPageChange={setHistoryPage}
                    onPerPageChange={(next) => {
                      setHistoryPerPage(next);
                      setHistoryPage(1);
                    }}
                    downloadingArtifactId={downloadingArtifactId}
                    onDownload={handleDownload}
                  />
                )
              ) : null}
            </main>

            <ReadinessRail
              selectedVariant={variant}
              onVariantChange={(next) => {
                setVariant(next);
                setHistoryPage(1);
              }}
              precheck={precheckQuery.data}
              precheckError={precheckQuery.error ?? null}
              canView={canViewVariant}
              canGenerate={canGenerateVariant}
              onGenerate={handleGenerate}
              isGenerating={generateMutation.isPending}
              downloadingArtifactId={downloadingArtifactId}
              downloadError={downloadError}
              onDownload={handleDownload}
              onShowIssues={anyReportAccess ? showIssues : undefined}
            />
          </div>

          <div className="fixed inset-x-0 bottom-0 z-20 border-t border-efs-border bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden">
            <div className="mx-auto flex max-w-screen-sm items-center justify-between gap-3">
              <div className="min-w-0 text-xs text-efs-text-muted">
                <p className="truncate">{t(`efs.reports.monthly.variants.${variant}.name`)}</p>
                <p>
                  {precheckQuery.data?.can_generate
                    ? t('efs.reports.monthly.readiness.ready')
                    : t('efs.reports.monthly.readiness.blocked')}
                </p>
              </div>
              <Button
                size="sm"
                disabled={
                  !precheckQuery.data?.can_generate ||
                  !canGenerateVariant(variant) ||
                  generateMutation.isPending
                }
                onClick={handleGenerate}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCcw className="mr-1 h-3 w-3" />
                )}
                {t('efs.reports.monthly.actions.generate')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyReportsPage;
